import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Loader2, GitMerge, AlertTriangle, CheckCircle2, RefreshCw, Layers,
  Folder, MapPin, UserCheck,
} from 'lucide-react';

const TABS = [
  {
    key: 'categories',
    label: 'Categorías',
    icon: Folder,
    apiPath: '/admin/categories',
    countLabel: (n) => `${n} equipo${n === 1 ? '' : 's'}`,
    impactLabel: 'Equipos involucrados',
  },
  {
    key: 'locations',
    label: 'Ubicaciones',
    icon: MapPin,
    apiPath: '/admin/locations',
    countLabel: (n) => `${n} unidad${n === 1 ? '' : 'es'}`,
    impactLabel: 'Unidades involucradas',
  },
  {
    key: 'responsibles',
    label: 'Responsables',
    icon: UserCheck,
    apiPath: '/admin/responsibles',
    countLabel: (n) => `${n} movimiento${n === 1 ? '' : 's'}`,
    impactLabel: 'Movimientos involucrados',
  },
];

const DuplicateMergeSection = ({ tab }) => {
  const { api } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);
  const [targets, setTargets] = useState({});
  const [confirmGroup, setConfirmGroup] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const apiInstance = api();
      const { data } = await apiInstance.get(`${tab.apiPath}/duplicates`);
      setGroups(data.groups || []);
      const defaults = {};
      (data.groups || []).forEach((g) => {
        defaults[g.normalized_name] = g.categories[0]?.id;
      });
      setTargets(defaults);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudieron cargar los duplicados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab.key]);

  const handleMerge = async () => {
    if (!confirmGroup) return;
    const targetId = targets[confirmGroup.normalized_name];
    if (!targetId) {
      toast.error('Selecciona el destino');
      return;
    }
    const sourceIds = confirmGroup.categories.map((c) => c.id).filter((id) => id !== targetId);
    if (sourceIds.length === 0) {
      toast.error('Nada que fusionar');
      return;
    }
    setMerging(true);
    try {
      const apiInstance = api();
      const { data } = await apiInstance.post(`${tab.apiPath}/merge`, {
        target_id: targetId,
        source_ids: sourceIds,
      });
      toast.success(
        `Fusionadas ${data.merged_count} entradas en "${data.target_name}". ${data.equipment_reassigned} referencias actualizadas.`
      );
      setConfirmGroup(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo fusionar');
    } finally {
      setMerging(false);
    }
  };

  const totalDuplicates = useMemo(
    () => groups.reduce((acc, g) => acc + g.categories.length - 1, 0),
    [groups]
  );
  const totalImpacted = useMemo(
    () => groups.reduce((acc, g) => acc + g.total_equipment, 0),
    [groups]
  );

  const Icon = tab.icon;

  return (
    <div className="space-y-6" data-testid={`merge-section-${tab.key}`}>
      <div className="flex justify-end">
        <Button variant="outline" onClick={load} disabled={loading} data-testid={`reload-${tab.key}-btn`}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Recargar
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Grupos con duplicados</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <Layers className="h-6 w-6 text-amber-400" />
              {groups.length}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Entradas extra a fusionar</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <AlertTriangle className="h-6 w-6 text-rose-400" />
              {totalDuplicates}
            </CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>{tab.impactLabel}</CardDescription>
            <CardTitle className="text-3xl flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              {totalImpacted}
            </CardTitle>
          </CardHeader>
        </Card>
      </div>

      {!loading && groups.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center space-y-3">
            <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
            <div className="text-lg font-medium">No hay {tab.label.toLowerCase()} duplicadas</div>
            <p className="text-sm text-muted-foreground">
              Tu catálogo de {tab.label.toLowerCase()} está limpio.
            </p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="py-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g) => {
          const targetId = targets[g.normalized_name];
          return (
            <Card key={g.normalized_name} data-testid={`merge-group-${tab.key}-${g.normalized_name}`}>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-primary" />
                      <Badge variant="secondary" className="text-xs uppercase tracking-wide">
                        {g.normalized_name}
                      </Badge>
                      <span className="text-base">
                        {g.categories.length} entradas · {tab.countLabel(g.total_equipment)}
                      </span>
                    </CardTitle>
                    <CardDescription>
                      Selecciona la entrada destino. Las demás se eliminarán y sus referencias pasarán al destino.
                    </CardDescription>
                  </div>
                  <Button
                    onClick={() => setConfirmGroup(g)}
                    disabled={!targetId || g.categories.length < 2}
                    data-testid={`merge-btn-${tab.key}-${g.normalized_name}`}
                  >
                    <GitMerge className="h-4 w-4 mr-2" />
                    Fusionar grupo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={targetId || ''}
                  onValueChange={(v) => setTargets((prev) => ({ ...prev, [g.normalized_name]: v }))}
                  className="space-y-2"
                >
                  {g.categories.map((c) => (
                    <Label
                      key={c.id}
                      htmlFor={`${tab.key}-${c.id}`}
                      className={`flex items-center justify-between gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        targetId === c.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border/50 hover:border-border'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <RadioGroupItem value={c.id} id={`${tab.key}-${c.id}`} />
                        <div className="min-w-0">
                          <div className="font-medium truncate">"{c.name}"</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            id: {c.id.slice(0, 8)}…
                          </div>
                        </div>
                      </div>
                      <Badge
                        className={
                          c.equipment_count > 0
                            ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                            : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
                        }
                      >
                        {tab.countLabel(c.equipment_count)}
                      </Badge>
                    </Label>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AlertDialog open={!!confirmGroup} onOpenChange={(v) => !v && setConfirmGroup(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fusionar {tab.label.toLowerCase()}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {confirmGroup && (() => {
                  const tid = targets[confirmGroup.normalized_name];
                  const target = confirmGroup.categories.find((c) => c.id === tid);
                  const sources = confirmGroup.categories.filter((c) => c.id !== tid);
                  return (
                    <>
                      <p>
                        Se reasignarán todas las referencias de las siguientes entradas y luego se eliminarán las origen:
                      </p>
                      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                        <div className="text-emerald-400 text-xs font-semibold uppercase">Destino</div>
                        <div>"{target?.name}" · {tab.countLabel(target?.equipment_count || 0)}</div>
                      </div>
                      <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                        <div className="text-rose-400 text-xs font-semibold uppercase">A eliminar ({sources.length})</div>
                        {sources.map((s) => (
                          <div key={s.id} className="text-sm">
                            "{s.name}" · {tab.countLabel(s.equipment_count)}
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Esta acción es auditada y reversible borrando manualmente la marca <code>is_deleted</code> en la BD.
                      </p>
                    </>
                  );
                })()}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={merging}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleMerge(); }}
              disabled={merging}
              data-testid={`merge-confirm-${tab.key}`}
            >
              {merging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Fusionar ahora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const CategoryMergePage = () => {
  const [activeTab, setActiveTab] = useState('categories');

  return (
    <div className="space-y-6" data-testid="duplicate-merge-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <GitMerge className="h-7 w-7 text-primary" />
          Fusionar duplicados
        </h1>
        <p className="text-muted-foreground mt-1">
          Detecta entradas con el mismo nombre normalizado (sin acentos, mayúsculas o espacios extra) y fusiónalas en una sola.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 max-w-md">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>
                <Icon className="h-4 w-4 mr-2" />
                {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-6">
            <DuplicateMergeSection tab={t} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default CategoryMergePage;
